/** 参数表格组：展示 HTML 构建 + Tab/折叠事件绑定（与 CMS tableTabs 同源） */
import { formatTableN, getRichContentUiLabels } from '@/components/shared/utils/richContentUiLabels'

import { parseHtmlFragment } from '@/components/shared/utils/promoteInlineStyles'

export const DEFAULT_COLLAPSE_ROWS = 8

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function normalizeCollapseRows(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 1) return DEFAULT_COLLAPSE_ROWS
  return Math.min(100, Math.floor(n))
}

function normalizeHideTitle(value) {
  return value === true || value === 'true' || value === '1' || value === 1
}

function shouldHideTableTabsTitle(hideTitle, panelCount) {
  return normalizeHideTitle(hideTitle) && Number(panelCount) === 1
}

function normalizeTableStyle(value) {
  return value === 'param' ? 'param' : 'legacy'
}

function tableStyleClassName(tableStyle) {
  return normalizeTableStyle(tableStyle) === 'param'
    ? 'editor-table editor-table--param'
    : 'editor-table'
}

/** 旧 HTML 表格升级为参数表样式（无 legacy 标记的一律按 param 处理） */
export function upgradeLegacyTablesInHtml(html) {
  if (!html || !/<table[\s>]/i.test(html)) return html
  try {
    const parsed = parseHtmlFragment(html, '__tbl_up')
    if (!parsed) return html
    const { root } = parsed
    let changed = false
    root.querySelectorAll('table').forEach((table) => {
      if (table.getAttribute('data-table-style') === 'legacy') return
      if (table.getAttribute('data-table-style') !== 'param') {
        table.setAttribute('data-table-style', 'param')
        changed = true
      }
      const cls = String(table.getAttribute('class') || '')
      if (!cls.includes('editor-table--param')) {
        const parts = new Set(cls.split(/\s+/).filter(Boolean))
        parts.add('editor-table')
        parts.add('editor-table--param')
        table.setAttribute('class', Array.from(parts).join(' '))
        changed = true
      }
    })
    return changed ? root.innerHTML : html
  } catch {
    return html
  }
}

/** 打开/展示时把 legacy 表格 JSON 升级为 param */
export function upgradeLegacyTablesInJson(doc) {
  if (!doc || typeof doc !== 'object') return false
  let changed = false
  const walk = (node) => {
    if (!node || typeof node !== 'object') return
    if (node.type === 'table') {
      if (!node.attrs) node.attrs = {}
      if (normalizeTableStyle(node.attrs.tableStyle) !== 'param') {
        node.attrs.tableStyle = 'param'
        changed = true
      }
    }
    const children = node.content
    if (Array.isArray(children)) children.forEach(walk)
  }
  walk(doc)
  return changed
}

function countTableRows(tableNode) {
  if (!tableNode?.content?.length) return 0
  return tableNode.content.filter((n) => n.type === 'tableRow').length
}

/** 给 table HTML 打上溢出行标记并包折叠容器 */
export function wrapTableForCollapse(tableHtml, rowCount, collapseRows = DEFAULT_COLLAPSE_ROWS) {
  if (!tableHtml) return ''
  const limit = normalizeCollapseRows(collapseRows)
  const needsCollapse = Number(rowCount) > limit
  let html = String(tableHtml)

  if (needsCollapse) {
    try {
      const parsed = parseHtmlFragment(html, '__wrap')
      const table = parsed?.root?.querySelector('table')
      if (table) {
        const rows = table.querySelectorAll(':scope > tbody > tr, :scope > thead > tr, :scope > tr')
        rows.forEach((tr, i) => {
          if (i >= limit) tr.classList.add('is-overflow-row')
          else tr.classList.remove('is-overflow-row')
        })
        html = parsed.root.innerHTML
      }
    } catch {
      // keep original html
    }
  }

  const expandLabel = escapeHtml(getRichContentUiLabels().expandTable)
  const toggle = needsCollapse
    ? `<button type="button" class="rich-table-toggle" aria-expanded="false" aria-label="${expandLabel}"><span class="rich-table-toggle__icon" aria-hidden="true"></span></button>`
    : ''
  return `<div class="rich-table-block" data-collapsed="${needsCollapse ? 'true' : 'false'}" data-collapse-rows="${limit}">${html}${toggle}</div>`
}

/** 在 DOM 中为尚未包裹的 table 补折叠容器 */
export function ensureTableCollapseWrappers(root, doc = document) {
  if (!root?.querySelectorAll) return
  root.querySelectorAll('table').forEach((table) => {
    if (table.closest('.rich-table-block')) return
    const rows = table.querySelectorAll(':scope > tbody > tr, :scope > thead > tr, :scope > tr')
    const limit = normalizeCollapseRows(
      table.closest('.table-tabs')?.getAttribute('data-collapse-rows') || DEFAULT_COLLAPSE_ROWS,
    )
    rows.forEach((tr, i) => {
      if (i >= limit) tr.classList.add('is-overflow-row')
    })
    const needsCollapse = rows.length > limit
    const wrap = doc.createElement('div')
    wrap.className = 'rich-table-block'
    wrap.setAttribute('data-collapse-rows', String(limit))
    wrap.setAttribute('data-collapsed', needsCollapse ? 'true' : 'false')
    table.parentNode?.insertBefore(wrap, table)
    wrap.appendChild(table)
    if (needsCollapse) {
      const btn = doc.createElement('button')
      btn.type = 'button'
      btn.className = 'rich-table-toggle'
      btn.setAttribute('aria-expanded', 'false')
      btn.setAttribute('aria-label', getRichContentUiLabels().expandTable)
      btn.innerHTML = '<span class="rich-table-toggle__icon" aria-hidden="true"></span>'
      wrap.appendChild(btn)
    }
  })
}

/**
 * 构建参数表格组展示 HTML
 * @param {object} node tableTabs JSON 节点
 * @param {(child: object) => string} renderChild
 * @param {{ colgroupHtml?: (tableNode) => string }} options
 */
export function buildTableTabsHtml(node, renderChild, options = {}) {
  const panels = (node?.content || []).filter((n) => n.type === 'tableTabPanel')
  if (!panels.length) return ''
  const collapseRows = normalizeCollapseRows(node.attrs?.collapseRows)
  const hideTitle = shouldHideTableTabsTitle(node.attrs?.hideTitle, panels.length)
  const bar = `<div class="table-tabs__bar">${panels.map((panel, i) => {
    const title = panel.attrs?.title || formatTableN(i)
    return `<button type="button" class="table-tabs__tab${i === 0 ? ' is-active' : ''}" data-index="${i}">${escapeHtml(title)}</button>`
  }).join('')}</div>`

  const panelsHtml = panels.map((panel, i) => {
    const tableNode = (panel.content || []).find((c) => c.type === 'table')
    let body = ''
    if (tableNode) {
      const style = normalizeTableStyle(tableNode.attrs?.tableStyle || 'param')
      const className = tableStyleClassName(style)
      const inner = (tableNode.content || []).map((child) => renderChild(child)).join('')
      const colgroup = typeof options.colgroupHtml === 'function' ? options.colgroupHtml(tableNode) : ''
      const tableHtml = `<table class="${className}" data-table-style="${style}" style="width:100%;max-width:100%;min-width:0">${colgroup}<tbody>${inner}</tbody></table>`
      body = wrapTableForCollapse(tableHtml, countTableRows(tableNode), collapseRows)
    } else {
      body = (panel.content || []).map((child) => renderChild(child)).join('')
    }
    return `<div class="table-tabs__panel${i === 0 ? ' is-active' : ''}" data-index="${i}"${i === 0 ? '' : ' style="display:none"'}>${body}</div>`
  }).join('')

  const hideAttr = hideTitle ? ' data-hide-title="true"' : ''
  return `<div class="table-tabs" data-type="table-tabs" data-collapse-rows="${collapseRows}"${hideAttr}>${bar}${panelsHtml}</div>`
}

/**
 * 把编辑器序列化的 table-tabs HTML（section 面板、无 Tab 条）补成展示态（含顶部切换条）。
 * 供热点条目富文本内嵌表格在门户切换使用。
 */
export function ensureTableTabsDisplayHtml(html) {
  if (!html || !String(html).includes('table-tabs')) {
    return html
  }
  try {
    const parsed = parseHtmlFragment(html, '__tt_root')
    if (!parsed) return html
    const { doc, root } = parsed
    root.querySelectorAll('[data-type="table-tabs"], .table-tabs').forEach((tabs) => {
      if (!(tabs instanceof HTMLElement)) return
      tabs.classList.add('table-tabs')
      tabs.setAttribute('data-type', 'table-tabs')
      const collapseRows = normalizeCollapseRows(tabs.getAttribute('data-collapse-rows'))
      tabs.setAttribute('data-collapse-rows', String(collapseRows))

      let panels = Array.from(
        tabs.querySelectorAll(':scope > [data-type="table-tab-panel"], :scope > .table-tabs__panel'),
      ).filter((el) => !el.classList.contains('table-tabs__bar'))

      const hideTitle = shouldHideTableTabsTitle(tabs.getAttribute('data-hide-title'), panels.length)
      if (hideTitle) tabs.setAttribute('data-hide-title', 'true')
      else tabs.removeAttribute('data-hide-title')

      let bar = tabs.querySelector(':scope > .table-tabs__bar')
      if (!bar) {
        bar = doc.createElement('div')
        bar.className = 'table-tabs__bar'
        panels.forEach((panel, i) => {
          const title = panel.getAttribute('data-title') || formatTableN(i)
          const btn = doc.createElement('button')
          btn.type = 'button'
          btn.className = `table-tabs__tab${i === 0 ? ' is-active' : ''}`
          btn.setAttribute('data-index', String(i))
          btn.textContent = title
          bar.appendChild(btn)
        })
        tabs.insertBefore(bar, tabs.firstChild)
      }

      panels = Array.from(
        tabs.querySelectorAll(':scope > [data-type="table-tab-panel"], :scope > .table-tabs__panel'),
      ).filter((el) => el !== bar && !el.classList.contains('table-tabs__bar'))

      panels.forEach((panel, i) => {
        panel.classList.add('table-tabs__panel')
        if (!panel.getAttribute('data-index')) panel.setAttribute('data-index', String(i))
        if (!panel.getAttribute('data-title')) {
          const tabBtn = bar?.querySelector(`.table-tabs__tab[data-index="${i}"]`)
          panel.setAttribute('data-title', tabBtn?.textContent?.trim() || formatTableN(i))
        }
        const active = i === 0
        panel.classList.toggle('is-active', active)
        panel.style.display = active ? '' : 'none'
      })
    })
    return root.innerHTML
  } catch {
    return html
  }
}

export function activateTableTab(root, index) {
  if (!root) return
  const tabs = Array.from(root.querySelectorAll('.table-tabs__tab'))
  const panels = Array.from(root.querySelectorAll('.table-tabs__panel'))
  tabs.forEach((el, i) => el.classList.toggle('is-active', i === index))
  panels.forEach((el, i) => {
    const active = i === index
    el.classList.toggle('is-active', active)
    el.style.display = active ? '' : 'none'
    if (active) {
      const block = el.querySelector('.rich-table-block')
      if (block?.querySelector('.is-overflow-row')) {
        block.setAttribute('data-collapsed', 'true')
        const btn = block.querySelector('.rich-table-toggle')
        if (btn) {
          btn.setAttribute('aria-expanded', 'false')
          btn.setAttribute('aria-label', getRichContentUiLabels().expandTable)
        }
      }
    }
  })
}

export function toggleTableCollapse(block) {
  if (!block) return
  const collapsed = block.getAttribute('data-collapsed') !== 'false'
  block.setAttribute('data-collapsed', collapsed ? 'false' : 'true')
  const btn = block.querySelector('.rich-table-toggle')
  if (btn) {
    const labels = getRichContentUiLabels()
    btn.setAttribute('aria-expanded', collapsed ? 'true' : 'false')
    btn.setAttribute('aria-label', collapsed ? labels.collapseTable : labels.expandTable)
  }
}

let staticTableTabsBound = false

/** 门户：document 级 click 委托（单例） */
export function bindStaticTableTabs() {
  if (staticTableTabsBound || typeof document === 'undefined') return
  staticTableTabsBound = true
  document.addEventListener('click', (event) => {
    const tab = event.target?.closest?.('.table-tabs__tab')
    if (tab) {
      const root = tab.closest('.table-tabs')
      if (!root) return
      event.preventDefault()
      const index = Number(tab.getAttribute('data-index'))
      if (Number.isFinite(index) && index >= 0) activateTableTab(root, index)
      return
    }

    const toggle = event.target?.closest?.('.rich-table-toggle')
    if (toggle) {
      const block = toggle.closest('.rich-table-block')
      if (!block) return
      event.preventDefault()
      toggleTableCollapse(block)
    }
  })
}

export { countTableRows, normalizeTableStyle, tableStyleClassName }
